import React, { useEffect, useState } from 'react'
import { Sparkles, X } from 'lucide-react'
import { authedFetch, getToken } from '@/lib/api';
import { tokens } from '@/design-system/tokens'

/**
 * UpdateBanner — polls /api/version every 60s.
 * When the version changes from what we booted with, shows a "New version" banner.
 *
 * D1d restyle (2026-05-06): tokens-based colours, lucide icon (no
 * unicode glyph), dismiss button, brand.gold strip with text.inverse.
 */
export function UpdateBanner() {
  const [showBanner, setShowBanner] = useState(false)
  const bootVersion = React.useRef<string | null>(null)

  useEffect(() => {
    const check = async () => {
      try {
        const r = await fetch('/api/version?t=' + Date.now())
        if (!r.ok) return
        const data = await r.json() as { version: string }
        if (!bootVersion.current) {
          bootVersion.current = data.version
          return
        }
        if (data.version !== bootVersion.current) {
          setShowBanner(true)
        }
      } catch {}
    }

    check()
    const id = setInterval(check, 60_000)
    return () => clearInterval(id)
  }, [])

  if (!showBanner) return null

  return (
    <div
      onClick={() => window.location.reload()}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 99999,
        background: tokens.color.brand.gold,
        color: tokens.color.text.inverse,
        textAlign: 'center',
        padding: '10px 16px',
        fontSize: 13,
        fontWeight: 600,
        cursor: 'pointer',
        boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
        fontFamily: tokens.typography.fontFamily.sans,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
        // Opt out of the desktop title-bar drag region. Without this, the
        // banner pins to the top of the window where Electron/Tauri have
        // -webkit-app-region: drag enabled by default — clicks fall through
        // to the OS drag handler and the user can never accept or dismiss.
        WebkitAppRegion: 'no-drag',
      } as React.CSSProperties}
    >
      <Sparkles size={14} />
      <span>New version available — tap to update</span>
      <button
        onClick={(e) => { e.stopPropagation(); setShowBanner(false) }}
        aria-label="Dismiss"
        style={{
          marginLeft: 6,
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          color: tokens.color.text.inverse,
          opacity: 0.7,
          display: 'inline-flex',
          alignItems: 'center',
          padding: 2,
          // Same opt-out so the X button is clickable on desktop.
          WebkitAppRegion: 'no-drag',
        } as React.CSSProperties}
      >
        <X size={14} />
      </button>
    </div>
  )
}
